import { createTheme } from "@mui/material/styles";

/**
 * The app's single theme definition.
 *
 * Used by both the main authenticated app and the public organisation pages -
 * the latter pass brand colours as overrides, so a district's page is the same
 * design with its own palette rather than a second, drifting theme.
 */

// shadcn-inspired color palette
const lightPalette = {
  primary: {
    main: "#000000",
    light: "#404040",
    dark: "#000000",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#71717a",
    light: "#a1a1aa",
    dark: "#52525b",
    contrastText: "#ffffff",
  },
  background: {
    default: "#ffffff",
    paper: "#ffffff",
  },
  text: {
    primary: "#000000",
    secondary: "#71717a",
    disabled: "#d4d4d8",
  },
  divider: "#e4e4e7",
  success: {
    main: "#16a34a",
    light: "#22c55e",
    dark: "#15803d",
  },
  warning: {
    main: "#ea580c",
    light: "#fb923c",
    dark: "#c2410c",
  },
  error: {
    main: "#dc2626",
    light: "#ef4444",
    dark: "#b91c1c",
  },
  info: {
    main: "#0284c7",
    light: "#0ea5e9",
    dark: "#0369a1",
  },
};

const darkPalette = {
  primary: {
    main: "#ffffff",
    light: "#e4e4e7",
    dark: "#a1a1aa",
    contrastText: "#000000",
  },
  secondary: {
    main: "#a1a1aa",
    light: "#d4d4d8",
    dark: "#71717a",
    contrastText: "#000000",
  },
  background: {
    default: "#09090b",
    paper: "#18181b",
  },
  text: {
    primary: "#fafafa",
    secondary: "#a1a1aa",
    disabled: "#52525b",
  },
  divider: "#27272a",
  success: {
    main: "#22c55e",
    light: "#4ade80",
    dark: "#16a34a",
  },
  warning: {
    main: "#fb923c",
    light: "#fdba74",
    dark: "#ea580c",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  info: {
    main: "#0ea5e9",
    light: "#38bdf8",
    dark: "#0284c7",
  },
};

/**
 * Decide readable text for a background colour, so a brand colour can be used
 * as a button or header fill without the label disappearing into it.
 * Standard sRGB relative luminance, with the usual 0.5 split point.
 */
export function contrastTextFor(hex, { light = "#ffffff", dark = "#000000" } = {}) {
  const parsed = normaliseHex(hex);
  if (!parsed) return light;

  const [r, g, b] = parsed;
  const channel = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  const luminance =
    0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

  return luminance > 0.5 ? dark : light;
}

/** "#abc" or "#aabbcc" -> [r, g, b]. Returns null for anything else. */
function normaliseHex(hex) {
  if (typeof hex !== "string") return null;

  let value = hex.trim().replace(/^#/, "");
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;

  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

/**
 * Build the MUI theme.
 *
 * @param {"light"|"dark"} mode
 * @param {object} overrides - optional brand colours
 *   { primary, secondary, accent } as hex strings; any that are absent keep
 *   the default palette's value.
 */
export function buildTheme(mode = "light", overrides = {}) {
  const base = mode === "light" ? lightPalette : darkPalette;
  const { primary, secondary, accent } = overrides || {};

  const palette = {
    mode,
    ...base,
    ...(primary && {
      primary: {
        main: primary,
        light: primary,
        dark: primary,
        contrastText: contrastTextFor(primary),
      },
    }),
    ...(secondary && {
      secondary: {
        main: secondary,
        light: secondary,
        dark: secondary,
        contrastText: contrastTextFor(secondary),
      },
    }),
    // The accent drives links and highlights rather than replacing a slot.
    ...(accent && {
      info: {
        main: accent,
        light: accent,
        dark: accent,
        contrastText: contrastTextFor(accent),
      },
    }),
  };

  return createTheme({
    palette,
    typography: {
      fontFamily:
        '"Roboto", "-apple-system", "BlinkMacSystemFont", "Segoe UI", sans-serif',
      h1: {
        fontSize: "2.25rem",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontSize: "1.875rem",
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontSize: "1.5rem",
        fontWeight: 700,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "0.875rem",
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.6,
        color: mode === "light" ? "#71717a" : "#a1a1aa",
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.5,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "0.375rem",
            fontSize: "0.875rem",
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "0.5rem",
            border: `1px solid ${mode === "light" ? "#e4e4e7" : "#27272a"}`,
            boxShadow:
              mode === "light"
                ? "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                : "0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "0.5rem",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: "0.375rem",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "0.375rem",
          },
        },
      },
    },
  });
}

export { lightPalette, darkPalette };
